import { typedApi } from "@/api/typed-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { Project } from "./queries";

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; description?: string }) => {
      const { data, error } = await typedApi.POST("/v1/projects", { body });
      if (error) throw new Error(JSON.stringify(error));
      // Justified: ProjectOut schema is compatible but openapi-fetch infers wider type
      return data as unknown as Project;
    },
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success(`Project "${p.name}" created`);
    },
    onError: (e) => toast.error(`Failed to create project: ${(e as Error).message}`),
  });
}

export function useRenameProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { data, error } = await typedApi.PATCH("/v1/projects/{project_id}", {
        params: { path: { project_id: id } },
        body: { name },
      });
      if (error) throw new Error(JSON.stringify(error));
      // Justified: PATCH response is {[key:string]:unknown} in openapi schema
      return data as unknown as Project;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Project renamed");
    },
    onError: (e) => toast.error(`Rename failed: ${(e as Error).message}`),
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await typedApi.DELETE("/v1/projects/{project_id}", {
        params: { path: { project_id: id } },
      });
      if (error) throw new Error(JSON.stringify(error));
      return null;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Project deleted");
    },
    onError: (e) => toast.error(`Delete failed: ${(e as Error).message}`),
  });
}
